
import { getPlatform } from "@utils/platformDetection.client";
import { getCityDetailsFromIp } from "@utils/cityDetection";
import { getCustomerStatus } from "@utils/customerStatusManager";
import {
  LEAD_CAPTURE_TOKEN,
  LEAD_CAPTURE_CARS24_TOKEN,
  OLX_TOKEN,
  OLX_MAIN,
  OLX_CHAT,
  OLX_CHAT_TOKEN,
  OLX_CENTRE,
  OLX_CENTRE_TOKEN,
  ACKODRIVE_LP,
  ACKODRIVE_LP_TOKEN,
  SEM_LP,
  SEM_LP_TOKEN,
  LeadSourceType,
} from "@constants/leadCapture";
import { LEAD_CAPTURE_URL } from "./endpoints";
import {
  LeadCapturePayload,
  LeadCaptureResponse,
} from "@interfaces/leadCapture.types";

/** IP + customer-status enrichment must not block lead submission indefinitely. */
const LEAD_ENRICHMENT_TIMEOUT_MS = 5_000;

async function getCityAndCustomerStatus(): Promise<
  [Awaited<ReturnType<typeof getCityDetailsFromIp>>, Awaited<ReturnType<typeof getCustomerStatus>> | null]
> {
  return Promise.race([
    Promise.all([getCityDetailsFromIp(), getCustomerStatus()]),
    new Promise((resolve) =>
      setTimeout(
        () => resolve([{ ip_city: null, mapped_city: null }, null] as [{ ip_city: null, mapped_city: null }, null]),
        LEAD_ENRICHMENT_TIMEOUT_MS
      )
    ),
  ]) as Promise<
    [Awaited<ReturnType<typeof getCityDetailsFromIp>>, Awaited<ReturnType<typeof getCustomerStatus>> | null]
  >;
}

/**
 * Get the appropriate token based on lead source
 */
const getTokenForLeadSource = (leadSrc: LeadSourceType): string => {
  switch (leadSrc) {
    case "Cars 24":
      return LEAD_CAPTURE_CARS24_TOKEN;
    case OLX_MAIN:
      return OLX_TOKEN;
    case OLX_CHAT:
      return OLX_CHAT_TOKEN;
    case OLX_CENTRE:
      return OLX_CENTRE_TOKEN;
    case ACKODRIVE_LP:
      return ACKODRIVE_LP_TOKEN;
    case SEM_LP:
      return SEM_LP_TOKEN;
    default:
      return LEAD_CAPTURE_TOKEN;
  }
};

/**
 * Post lead data to the lead capture API
 */
export const postLead = async (
  payload: LeadCapturePayload,
  leadSrc?: LeadSourceType
): Promise<LeadCaptureResponse> => {
  const url = LEAD_CAPTURE_URL;
  const token = leadSrc ? getTokenForLeadSource(leadSrc) : LEAD_CAPTURE_TOKEN;
  const platform = getPlatform();

  try {
    const [cityDetails, customerStatus] = await getCityAndCustomerStatus();

    // Prepare final payload
    const finalPayload: any = {
      ...payload,
      platform,
      dump_data: {
        ...(payload.dump_data || {}),
        mx_Is_Active_Policy_Holder: customerStatus?.is_active_policy_holder,
        mx_Is_Acko_Customer: customerStatus?.is_acko_customer,
        mx1_ACKO_Active_Policies: customerStatus?.active_policies,
        mx_ACKO_Expired_Policies: customerStatus?.expired_policies,
        mx_ACKO_User: customerStatus?.acko_user,
        mx_ACKO_Vehicle_Number: customerStatus?.old_vehicle_numbers,
        mx_ACKO_Model: customerStatus?.car_details,
        mx_ACKO_Model_Registration_Year: customerStatus?.car_registration,
      },
    };

    if (cityDetails?.ip_city) {
      finalPayload.ip_city = cityDetails.ip_city;
    }

    if (cityDetails?.mapped_city) {
      finalPayload.mapped_city = cityDetails.mapped_city;
    }

      // Make API request
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(finalPayload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error: any) {
    console.error("Lead capture API error:", error?.message);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : "Unknown error",
      },
    };
  }
}; 